// SEQ-180 phase 2, P22: the suggestion lists behind the Project manager and
// Site manager fields on NewProject and EditProjectModal.
//
// D22 chose free text over a foreign key to profiles: a site manager may
// have no login, and a FK would force user creation as a side effect of
// naming someone. Free text alone drifts ("J. Smith", "John Smith", "john
// smith"), so both fields carry an HTML datalist of the values already in
// use in the organisation. Typing is never blocked; the list only makes the
// existing spelling the easy one to pick.
//
// PostgREST has no DISTINCT, so both columns are selected and deduplicated
// here. The organisation has tens of projects, not thousands, so one small
// select is cheaper than adding an RPC for it.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

export interface ProjectPeopleOptions {
  /** Distinct non-blank `claims_projects.project_manager` values in this organisation, case-insensitively deduplicated, sorted. */
  projectManagers: string[];
  /** Distinct non-blank `claims_projects.site_manager` values in this organisation, case-insensitively deduplicated, sorted. */
  siteManagers: string[];
  loading: boolean;
  /** Fetch failure message, or null. A failure only costs the suggestions, never the ability to type a name. */
  error: string | null;
  refresh: () => void;
}

/** Keeps the first spelling seen for a name and drops later case variants of it, so the list suggests one "John Smith" rather than three. */
function distinctNames(values: Array<string | null | undefined>): string[] {
  const seen = new Map<string, string>();
  for (const raw of values) {
    const name = (raw ?? '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (!seen.has(key)) seen.set(key, name);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b, 'en-AU'));
}

export function useProjectPeopleOptions(enabled: boolean = true): ProjectPeopleOptions {
  const { organizationId } = useAuth();
  const [projectManagers, setProjectManagers] = useState<string[]>([]);
  const [siteManagers, setSiteManagers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    if (!enabled || !organizationId) return;
    let cancelled = false;

    async function load(orgId: string) {
      setLoading(true);
      setError(null);
      const { data, error: queryError } = await supabase
        .from('claims_projects')
        .select('project_manager, site_manager')
        .eq('organization_id', orgId);

      if (cancelled) return;
      if (queryError) {
        setError(queryError.message);
        setLoading(false);
        return;
      }
      const rows = data ?? [];
      setProjectManagers(distinctNames(rows.map((r) => r.project_manager)));
      setSiteManagers(distinctNames(rows.map((r) => r.site_manager)));
      setLoading(false);
    }

    load(organizationId);
    return () => {
      cancelled = true;
    };
  }, [organizationId, enabled, reloadToken]);

  return { projectManagers, siteManagers, loading, error, refresh };
}
